import type { ApiError } from "./api-client";

/**
 * Normalised view of an apiFetch failure. The api-client throws an `Error`
 * augmented with `.status` (HTTP status) and `.problem` (RFC-7807 body, German
 * `detail`). This flattens the unknown caught value into a typed shape callers
 * can branch on (e.g. 412 -> "jemand war schneller", DE-119 / ADR-FE-006).
 */
export interface ApiFailure {
  status: number | undefined;
  problem: ApiError | undefined;
  message: string;
}

function isApiError(value: unknown): value is ApiError {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.type === "string" &&
    typeof v.title === "string" &&
    typeof v.status === "number" &&
    typeof v.detail === "string"
  );
}

export function toApiFailure(err: unknown): ApiFailure {
  if (err instanceof Error) {
    const augmented = err as Error & { status?: unknown; problem?: unknown };
    return {
      status: typeof augmented.status === "number" ? augmented.status : undefined,
      problem: isApiError(augmented.problem) ? augmented.problem : undefined,
      message: err.message,
    };
  }
  return { status: undefined, problem: undefined, message: String(err) };
}
